import {
  APPLICATION_LEVEL_NODE_ID,
  type DomainTree,
} from './domain-tree.utils';

type EndpointSignalCount = {
  endpointId: string | null;
  signalCount: number;
};

const hasSelectedAncestor = (
  nodeId: string,
  selectedNodeIds: Set<string>,
  domainTree: DomainTree,
): boolean => {
  for (
    let node = domainTree.nodeMap.get(nodeId)?.parent;
    node;
    node = node.parent
  ) {
    if (selectedNodeIds.has(node.id)) {
      return true;
    }
  }
  return false;
};

export function buildDomainTreeSignalCounts({
  domainTree,
  endpointCounts,
}: {
  domainTree: DomainTree | null;
  endpointCounts: EndpointSignalCount[];
}): Record<string, number> {
  if (!domainTree) {
    return {};
  }

  const nodeIdToCountMap: Record<string, number> = {};

  const { endpointSignalCounts, nullEndpointCount } = endpointCounts.reduce(
    (accumulator, count) => {
      if (count.endpointId) {
        accumulator.endpointSignalCounts.set(
          count.endpointId,
          count.signalCount,
        );
      } else {
        accumulator.nullEndpointCount += count.signalCount;
      }
      return accumulator;
    },
    {
      endpointSignalCounts: new Map<string, number>(),
      nullEndpointCount: 0,
    },
  );

  const calculateNodeCount = (node: DomainTree['roots'][number]): number => {
    const directEndpointTotal = [...new Set(node.directEndpointIds)].reduce(
      (totalCount, endpointId) =>
        totalCount + (endpointSignalCounts.get(endpointId) ?? 0),
      0,
    );

    const totalCount =
      directEndpointTotal +
      node.children.reduce(
        (childTotal, child) => childTotal + calculateNodeCount(child),
        0,
      );

    nodeIdToCountMap[node.id] = totalCount;

    return totalCount;
  };

  domainTree.roots.map((rootNode) => calculateNodeCount(rootNode));

  nodeIdToCountMap[APPLICATION_LEVEL_NODE_ID] = nullEndpointCount;

  return nodeIdToCountMap;
}

export function sumDomainTreeSelectionSignalCounts({
  selectedNodeIds,
  nodeIdToCountMap,
  domainTree,
}: {
  selectedNodeIds: string[];
  nodeIdToCountMap: Record<string, number> | undefined;
  domainTree?: DomainTree | null;
}): number | undefined {
  if (!nodeIdToCountMap || selectedNodeIds.length === 0) {
    return undefined;
  }

  const selectedNodeIdSet = new Set(selectedNodeIds);
  const nodesToSum =
    domainTree && selectedNodeIds.length > 1
      ? selectedNodeIds.filter(
          (nodeId) =>
            !hasSelectedAncestor(nodeId, selectedNodeIdSet, domainTree),
        )
      : selectedNodeIds;

  let total = 0;
  let hasMappedCount = false;

  for (const nodeId of nodesToSum) {
    const count = nodeIdToCountMap[nodeId];
    if (count === undefined) {
      continue;
    }
    hasMappedCount = true;
    total += count;
  }

  return hasMappedCount ? total : undefined;
}
